import React, { useState, useEffect, useCallback } from 'react';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  TextField,
  Button,
  Avatar,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { taskDataService } from '../../../services/taskDataService';

// İkonlar
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import SendIcon from '@mui/icons-material/Send';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ImageIcon from '@mui/icons-material/Image';
import CommentIcon from '@mui/icons-material/Comment';
import PendingIcon from '@mui/icons-material/Pending';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Stil tanımlamaları
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  }
}));

const StatusChip = styled(Chip)<{ customcolor?: string }>(({ theme, customcolor }) => ({
  fontWeight: 'bold',
  backgroundColor: customcolor ? `${customcolor}20` : undefined,
  color: customcolor,
  borderColor: customcolor ? `${customcolor}50` : undefined,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

// Task tipi tanımlaması
interface Task {
  id: string;
  name: string;
  description: string;
  status: string;
  completionType: string;
  createdAt: number;
  deadline?: string;
  isRecurring: boolean;
  imageUrls?: string[];
  personnelId?: string;
  personnelName?: string;
  groupId?: string;
  groupName?: string;
  branchesId?: string | null;
  branchName?: string;
  acceptedAt?: number;
  startedAt?: number;
  completedAt?: number;
  delayReason?: string;
  companyId?: string; // Şirket ID'si
  startTolerance?: number; // Tekrarlı görevler için tolerans süresi (dakika)
  repetitionTimes?: string[]; // Tekrarlı görevlerin zamanları
  requestCompletionPhoto?: boolean;
  completionPhotoUrl?: string;
}

interface Comment {
  id: string;
  text: string;
  createdAt: number;
  userName: string;
  userId: string;
}

interface TaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  task: Task | null;
  comments?: Comment[];
  onAddComment?: (text: string) => Promise<void>;
  loading?: boolean;
  currentUserId?: string; // Mevcut kullanıcının kimliği
  currentUserName?: string; // Mevcut kullanıcının adı
  companyId?: string; // Şirket ID'si
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  open,
  onClose,
  task,
  comments = [],
  onAddComment,
  loading = false,
  currentUserId = "current-user", // Test için varsayılan kullanıcı kimliği
  currentUserName = "", // Varsayılan değer boş string olsun
  companyId = "" // Şirket ID'si
}) => {
  // ✅ AuthContext'ten userDetails VE personel bilgisi al
  const { userDetails, getPersonnelInfo, getPersonnelName } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [localComments, setLocalComments] = useState<Comment[]>(comments);
  const [groupName, setGroupName] = useState<string>('');
  const [loadingGroupName, setLoadingGroupName] = useState<boolean>(false);
  const [personnelName, setPersonnelName] = useState<string>('');
  const [loadingPersonnelName, setLoadingPersonnelName] = useState<boolean>(false);
  const [realUserName, setRealUserName] = useState<string>('');
  const [isCompletionPhotoFullscreenOpen, setIsCompletionPhotoFullscreenOpen] = useState<boolean>(false);

  // Kullanıcının kaç yorum yaptığını kontrol et
  const userCommentCount = currentUserId ? 
    localComments.filter(comment => comment.userId === currentUserId).length : 0;
  const canAddComment = userCommentCount < 2;

  // Dışarıdan gelen yorumları güncelle
  useEffect(() => {
    // İlk render'da veya modal açıldığında yorumları yükle
    if (open) {
      setLocalComments(comments);
    }
  }, [open]); // Sadece modal açıldığında çalışsın - diziyi bağımlılık olarak eklemeyin

  // Yorumları veritabanından çeken fonksiyon
  const fetchComments = useCallback(async () => {
    if (!task?.id || !open) return;
    
    try {
      // Şirket ID'sini belirle - sadece task ve props kullan, localStorage kullanma
      let taskCompanyId = task.companyId || companyId;
      
      // Şirket ID'si bulunamadıysa ve görevde branchesId varsa, ondan şirket ID'sini çıkarmaya çalış
      if (!taskCompanyId && task.branchesId) {
        try {
          const branchIdParts = String(task.branchesId).split('/');
          if (branchIdParts.length > 0) {
            taskCompanyId = branchIdParts[0];
          }
        } catch (e) {
          console.error('branchesId işlenirken hata:', e);
        }
      }
      
      // Hala şirket ID'si bulunamazsa işlemi sonlandır
      if (!taskCompanyId) {
        console.error('Şirket ID bulunamadı, yorumlar çekilemiyor.');
        return;
      }
      
      // SQL'den görevin yorumlarını al (taskDataService)
      const commentsData = await taskDataService.getComments(Number(task.id));
      const commentsArray = (commentsData || []).map((c) => ({
        id: String(c.id),
        text: c.text,
        userId: c.userUid || '',
        userName: c.userName || 'Kullanıcı',
        createdAt: c.createdAt ? new Date(c.createdAt).getTime() : Date.now(),
      }));
      commentsArray.sort((a, b) => b.createdAt - a.createdAt);
      setLocalComments(commentsArray);
    } catch (error) {
      console.error('Yorumlar alınırken hata:', error);
    }
  }, [task?.id, task?.companyId, task?.branchesId, companyId, open]);

  // Grup bilgilerini veritabanından çek
  const fetchGroupName = useCallback(async () => {
    if (!task?.groupId || !open) return;
    
    // Halihazırda bir groupName varsa çekmeye gerek yok
    if (task.groupName) {
      setGroupName(task.groupName);
      return;
    }
    
    // Yükleme durumunu güncelle
    setLoadingGroupName(true);
    
    try {
      // Şirket ID'sini belirle - sadece task ve props kullan, localStorage kullanma
      let taskCompanyId = task.companyId || companyId;
      
      // Şirket ID'si bulunamadıysa ve görevde branchesId varsa, ondan şirket ID'sini çıkarmaya çalış
      if (!taskCompanyId && task.branchesId) {
        try {
          const branchIdParts = String(task.branchesId).split('/');
          if (branchIdParts.length > 0) {
            taskCompanyId = branchIdParts[0];
          }
        } catch (e) {
          console.error('branchesId işlenirken hata:', e);
        }
      }
      
      // Hala şirket ID'si bulunamazsa işlemi sonlandır
      if (!taskCompanyId) {
        console.error('Şirket ID bulunamadı, grup bilgisi çekilemiyor.');
        setLoadingGroupName(false);
        return;
      }
      
      // SQL'den görev grubu bilgisini al (taskDataService)
      const groupData = await taskDataService.getGroup(Number(task.groupId));
      if (groupData) {
        setGroupName(groupData.name || `Grup ${task.groupId}`);
      } else {
        setGroupName(`ID: ${task.groupId}`);
      }
    } catch (error) {
      console.error('Görev grubu bilgileri alınırken hata:', error);
      setGroupName(`ID: ${task.groupId}`);
    } finally {
      setLoadingGroupName(false);
    }
  }, [task?.groupId, task?.groupName, task?.companyId, task?.branchesId, companyId, open]);

  // Personel bilgilerini veritabanından çek
  const fetchPersonnelName = useCallback(async () => {
    if (!task?.personnelId || !open) return;
    
    // Halihazırda bir personnelName varsa çekmeye gerek yok
    if (task.personnelName) {
      setPersonnelName(task.personnelName);
      return;
    }
    
    // Yükleme durumunu güncelle
    setLoadingPersonnelName(true);
    
    try {
      // Şirket ID'sini belirle - sadece task ve props kullan
      let taskCompanyId = task.companyId || companyId;
      
      // Şirket ID'si bulunamadıysa ve görevde branchesId varsa, ondan şirket ID'sini çıkarmaya çalış
      if (!taskCompanyId && task.branchesId) {
        try {
          const branchIdParts = String(task.branchesId).split('/');
          if (branchIdParts.length > 0) {
            taskCompanyId = branchIdParts[0];
          }
        } catch (e) {
          console.error('branchesId işlenirken hata:', e);
        }
      }
      
      // Hala şirket ID'si bulunamazsa işlemi sonlandır
      if (!taskCompanyId) {
        console.error('Şirket ID bulunamadı, personel bilgisi çekilemiyor.');
        setLoadingPersonnelName(false);
        return;
      }
      
      // ✅ Önce AuthContext personel cache'inden dene
      const cachedPersonnel = getPersonnelInfo(task.personnelId);
      if (cachedPersonnel?.name) {
        setPersonnelName(cachedPersonnel.name);
        setLoadingPersonnelName(false);
        return;
      }
      
      // Fallback: Cloud Functions ile personel bilgilerini al
      let personnelData = await firebaseDataService.getPersonnel(taskCompanyId, task.personnelId);
      
      if (personnelData && (personnelData as any).name) {
        setPersonnelName((personnelData as any).name);
        setLoadingPersonnelName(false);
        return;
      }
      
      // Cloud Functions ile users'dan dene
      const userData = await firebaseDataService.getUserData(task.personnelId);
      
      if (userData) {
        const username = (userData as any).displayName || (userData as any).fullName || (userData as any).name;
        if (username) {
          setPersonnelName(username);
          setLoadingPersonnelName(false);
          return;
        }
      }
      
      // Hiçbir yöntemle bulunamadıysa ID olarak göster
      setPersonnelName(`ID: ${task.personnelId}`);
    } catch (error) {
      console.error('Personel bilgileri alınırken hata:', error);
      setPersonnelName(`ID: ${task.personnelId}`);
    } finally {
      setLoadingPersonnelName(false);
    }
  }, [task?.personnelId, task?.personnelName, task?.companyId, task?.branchesId, companyId, open]);

  // Modal açıldığında grup ve personel bilgilerini çek
  useEffect(() => {
    if (open && task) {
      if (task.groupId) {
        fetchGroupName();
      }
      if (task.personnelId) {
        fetchPersonnelName();
      }
    }
  }, [open, task, fetchGroupName, fetchPersonnelName]);

  // Modal açıldığında yorumları çek
  useEffect(() => {
    if (open && task?.id) {
      fetchComments();
    }
  }, [open, task?.id, fetchComments]);

  // Yorum metnini kontrol et - bu fonksiyonu daha verimli hale getirelim
  useEffect(() => {
    const isError = commentText.length > 300;
    
    if (isError && !commentError) {
      setCommentError("Yorum en fazla 300 karakter olabilir");
    } else if (!isError && commentError) {
      setCommentError(null);
    }
  }, [commentText, commentError]);

  // ✅ Kullanıcının gerçek adını AuthContext cache'inden al
  const getUserRealName = useCallback(async () => {
    if (!currentUserId || currentUserId === "current-user") return;
    
    try {
      // ✅ Önce AuthContext userDetails'den dene (giriş yapmış kullanıcı için)
      if (userDetails && currentUserId === userDetails.id) {
        const fullName = `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim();
        if (fullName) {
          setRealUserName(fullName);
          return;
        }
      }
      
      // ✅ Personel cache'inden dene
      const personnelInfo = getPersonnelInfo(currentUserId);
      if (personnelInfo?.name) {
        setRealUserName(personnelInfo.name);
        return;
      }
      
      // Fallback: Cloud Functions ile kullanıcı bilgilerini al
      const userData = await firebaseDataService.getUserData(currentUserId);
      
      if (userData) {
        // firstName ve lastName kontrol et ve birleştir
        if ((userData as any).firstName && (userData as any).lastName) {
          setRealUserName(`${(userData as any).firstName} ${(userData as any).lastName}`);
        } else if ((userData as any).displayName) {
          setRealUserName((userData as any).displayName);
        } else if ((userData as any).fullName) {
          setRealUserName((userData as any).fullName);
        } else {
          // Uygun isim bulunamadıysa, prop olarak gelen currentUserName ile devam et
          setRealUserName(currentUserName);
        }
      } else {
        // Kullanıcı bulunamadıysa, prop olarak gelen currentUserName ile devam et
        setRealUserName(currentUserName);
      }
    } catch (error) {
      console.error('Kullanıcı adı alınırken hata:', error);
      setRealUserName(currentUserName); // Hata durumunda currentUserName kullan
    }
  }, [currentUserId, currentUserName]);

  // Modal açıldığında kullanıcı adını çek
  useEffect(() => {
    if (open && currentUserId) {
      getUserRealName();
    }
  }, [open, currentUserId, getUserRealName]);

  // Tek bir useEffect içinde tüm localStorage işlemlerini yapalım ve sadece bir kez çalıştıralım
  useEffect(() => {
    // Modal kapalıysa hiçbir şey yapma
    if (!open || !task) return;
    
    // Temizleme fonksiyonu
    const cleanup = () => {
      // Modal kapandığında state'leri temizle
      setCurrentImageIndex(0);
    };
    
    try {
      // Task'ın kendi companyId'si varsa sakla
      // localStorage kullanımını kaldırıyoruz
    } catch (e) {
      console.error('Erişim sırasında hata:', e);
    }
    
    // Modal kapatıldığında temizleme işlemi yap
    return cleanup;
  }, [open, task?.id]); // Sadece modal açıldığında/kapandığında çalışsın

  // Componentin mount edildiğinde kullanıcı adını loglamak için useEffect ekleyelim
  useEffect(() => {
    console.log('-------------------------');
    console.log('TaskDetailModal - Kullanıcı bilgileri:');
    console.log('- currentUserName (props):', currentUserName);
    console.log('- realUserName (Firebase):', realUserName);
    console.log('-------------------------');
  }, [currentUserName, realUserName]);

  // Durum rengini döndürme
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return '#FFA000'; // Turuncu
      case 'accepted':
        return '#1976D2'; // Mavi
      case 'started':
        return '#43A047'; // Yeşil
      case 'completed':
        return '#6A1B9A'; // Mor
      default:
        return '#757575'; // Gri
    }
  };

  // Görev durumuna göre ikon döndürme
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'accepted':
        return <TaskAltIcon />;
      case 'started':
        return <PlayCircleFilledIcon />;
      case 'completed':
        return <CheckCircleIcon />;
      default:
        return <PendingIcon />;
    }
  };

  // Görev durumuna göre etiket döndürme
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'accepted':
        return 'Kabul Edildi';
      case 'started':
        return 'Başlatıldı';
      case 'completed':
        return 'Tamamlandı';
      default:
        return 'Bilinmiyor';
    }
  };

  // Tarih formatını düzenle
  const formatDate = (timestamp: number | undefined): string => {
    if (!timestamp) return '-';
    
    try {
      // Timestamp olarak gelirse doğrudan kullan
      if (typeof timestamp === 'number') {
        return format(new Date(timestamp), 'dd MMMM yyyy HH:mm', { locale: tr });
      }
      
      // Diğer durumlar için kontrol
      return format(new Date(timestamp), 'dd MMMM yyyy HH:mm', { locale: tr });
    } catch (error) {
      console.error('Tarih formatlanırken hata oluştu:', error);
      return String(timestamp) || '-';
    }
  };

  // getTaskDate fonksiyonunu memoize edelim, böylece her render'da yeniden hesaplanmaz
  const memoizedGetTaskDate = useCallback((dateType: 'acceptedAt' | 'startedAt' | 'completedAt'): number | undefined => {
    if (!task) return undefined;
    
    // 1. Task nesnesinden doğrudan kontrol et
    if (task[dateType]) {
      return task[dateType] as number;
    }
    
    // 2. Status değerine göre bazı varsayılan değerler belirle
    // Önce tamamlanma tarihini kontrol edelim (eğer varsa)
    const actualCompletedAt = task.completedAt;
    
    // Oluşturulma tarihini başlangıç noktası olarak alalım
    const creationTime = task.createdAt;
    
    // Mantıklı bir tarih sıralaması oluşturalım
    // Oluşturulma < Kabul edilme < Başlama < Tamamlanma
    
    if (dateType === 'acceptedAt' && task.status !== 'pending') {
      // Kabul edilme tarihi, oluşturulma tarihinden 2 dakika sonra olsun
      const suggestedTime = creationTime + 120000; // 2 dakika sonra
      
      // Eğer tamamlanma tarihi varsa ve önerilen kabul tarihi bundan sonraysa düzelt
      if (actualCompletedAt && suggestedTime >= actualCompletedAt) {
        return actualCompletedAt - 300000; // Tamamlanmadan 5 dakika önce
      }
      
      return suggestedTime;
    }
    
    if (dateType === 'startedAt' && (task.status === 'started' || task.status === 'completed')) {
      // Önce kabul edilme tarihini alalım - recursive çağrı yapma!
      let acceptedTime = task.acceptedAt;
      
      // Eğer gerçek acceptedAt değeri yoksa, hesaplama yap (recursive çağrı olmadan)
      if (!acceptedTime) {
        // Kabul edilme zamanını, oluşturulma zamanından 2 dakika sonra olarak hesapla
        acceptedTime = creationTime + 120000;
        
        // Eğer tamamlanma tarihi varsa ve hesaplanan kabul tarihi bundan sonraysa düzelt
        if (actualCompletedAt && acceptedTime >= actualCompletedAt) {
          acceptedTime = actualCompletedAt - 300000; // Tamamlanmadan 5 dakika önce
        }
      }
      
      if (!acceptedTime) {
        // Kabul edilme tarihi yoksa ve tamamlanma tarihi varsa, tamamlanmadan 3 dakika önce başlamış olsun
        if (actualCompletedAt) {
          return actualCompletedAt - 180000; // Tamamlanmadan 3 dakika önce
        }
        return undefined;
      }
      
      // Başlama tarihi, kabul edilme tarihinden en az 1 dakika sonra olmalı
      let suggestedTime = acceptedTime + 60000; // 1 dakika sonra
      
      // Eğer tamamlanma tarihi varsa başlama tarihi tamamlanmadan önce olmalı
      if (actualCompletedAt) {
        // Eğer önerilen başlama zamanı tamamlanma zamanından sonraysa düzelt
        if (suggestedTime >= actualCompletedAt) {
          suggestedTime = actualCompletedAt - 120000; // Tamamlanmadan 2 dakika önce
          
          // Şimdi de başlama zamanı kabul edilme zamanından önceyse düzelt
          if (suggestedTime <= acceptedTime) {
            suggestedTime = acceptedTime + 30000; // Kabul edildikten 30 saniye sonra
            
            // Son bir kontrol, hala tamamlanma zamanından sonraysa
            if (suggestedTime >= actualCompletedAt) {
              // Kabul edilme ile tamamlanma arasına sıkıştır
              suggestedTime = acceptedTime + Math.floor((actualCompletedAt - acceptedTime) / 2);
            }
          }
        }
      }
      
      return suggestedTime;
    }
    
    // Tamamlanma tarihi zaten task'tan geliyor, ekstra bir şey yapmamıza gerek yok
    return undefined;
  }, [task]);

  // Veritabanına yorum ekleyen fonksiyon
  const addCommentToDatabase = async (commentText: string): Promise<boolean> => {
    console.log('-------------TASK DETAIL MODAL-------------');
    console.log('addCommentToDatabase - Yorum ekleme işlemi başlıyor:');
    
    if (!task || !task.id) {
      console.error('Görev ID bulunamadı!');
      return false;
    }
    
    console.log('task.id:', task.id);
    console.log('task:', task);
    
    // Şirket ID'sini belirle - öncelik sırası: 
    // 1. Task nesnesindeki companyId 
    // 2. Prop olarak gelen companyId
    let taskCompanyId = task.companyId || companyId;
    console.log('İlk kontrol:');
    console.log('- task.companyId:', task.companyId);
    console.log('- props companyId:', companyId);
    console.log('- Birleştirilmiş taskCompanyId:', taskCompanyId);
    
    // Hala bulunamadıysa task.branchesId'yi deneyelim
    if (!taskCompanyId && task.branchesId) {
      try {
        const branchIdParts = String(task.branchesId).split('/');
        if (branchIdParts.length > 0) {
          taskCompanyId = branchIdParts[0];
          console.log('Şirket ID task.branchesId\'den çıkarıldı:', taskCompanyId);
        }
      } catch (e) {
        console.error('branchesId işlenirken hata:', e);
      }
    }
    
    // Hala bulunamadıysa console'da hataları göster ve iptal et
    if (!taskCompanyId) {
      console.error('Şirket ID bulunamadı! Yorum eklenemez.');
      console.error('Task:', task);
      console.error('Props companyId:', companyId);
      console.error('---------------------------------------------');
      return false;
    }

    console.log(`Yorum eklemek için kullanılacak şirket ID: ${taskCompanyId}, görev ID: ${task.id}`);

    try {
      // Yorumu SQL'e ekle (taskDataService)
      await taskDataService.addComment(Number(task.id), {
        text: commentText,
        userUid: currentUserId,
        userName: realUserName || currentUserName || 'Kullanıcı',
      });
      return true;
    } catch (error) {
      console.error('Yorum veritabanına eklenirken hata:', error);
      return false;
    }
  };

  // Yorum gönderme
  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    if (commentText.length > 300) return;
    if (!canAddComment) return;

    try {
      setSubmitting(true);
      
      // Veritabanına kaydet
      const savedToDatabase = await addCommentToDatabase(commentText);
      
      if (savedToDatabase) {
        // Eğer dışarıdan bir onAddComment fonksiyonu geldiyse çağır
        if (onAddComment) {
          await onAddComment(commentText);
        }
        
        // Yorumları yeniden yükle
        await fetchComments();
        
        // Yorum alanını temizle
        setCommentText('');
      } else {
        throw new Error('Yorum veritabanına kaydedilemedi');
      }
    } catch (error) {
      console.error('Yorum gönderilirken hata:', error);
      alert('Yorum kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  // Resimlerin görüntülenmesi
  const handleNextImage = () => {
    if (task?.imageUrls && currentImageIndex < task.imageUrls.length - 1) {
      setCurrentImageIndex(prevIndex => prevIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prevIndex => prevIndex - 1);
    }
  };

  if (!task) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="task-detail-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          height: { xs: 'auto', md: '90vh' } // Mobilde otomatik, masaüstünde 90vh yükseklik
        }
      }}
    >
      <DialogTitle 
        id="task-detail-dialog-title"
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 1.5
        }}
      >
        <Typography variant="h6" fontWeight="bold">Görev Detayları</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 5, overflow: 'hidden', maxHeight: 'calc(90vh - 64px)' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ height: '100%' }}>
            {/* Sol taraf - Görev bilgileri - sabit kalacak */}
            <Grid item xs={12} md={8} sx={{ 
              height: '100%', 
              overflowY: 'auto',
              pr: { md: 2 },
              // Webkit tarayıcılar için kaydırma çubuğu stilleri
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bdbdbd',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0a0a0',
              },
            }}>
              {/* Görev başlığı */}
              <Box mb={2} mt={3}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {task.name}
                </Typography>
                <Box display="flex" alignItems="center" mb={1}>
                  <StatusChip
                    icon={getStatusIcon(task.status)}
                    label={getStatusLabel(task.status)}
                    customcolor={getStatusColor(task.status)}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              {/* Görev açıklaması */}
              <Paper 
                variant="outlined" 
                sx={{ p: 2, mb: 3, minHeight: '150px', borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <CommentIcon /> Görev Açıklaması
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />
                <Typography 
                  variant="body2" 
                  whiteSpace="pre-wrap"
                  sx={{ 
                    fontSize: '0.875rem',
                    lineHeight: '1.3',
                  }}
                >
                  {task.description || 'Açıklama bulunmuyor.'}
                </Typography>
              </Paper>

              {/* Görev resimleri - sol tarafa taşındı */}
              {task.imageUrls && task.imageUrls.length > 0 && (
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2 }}
                >
                  <SectionTitle variant="subtitle1">
                    <ImageIcon /> Görev Resimleri ({task.imageUrls.length})
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />

                  {/* Resim küçük önizlemeleri - küçük resimler yan yana */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {task.imageUrls.map((url, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 100,
                          height: 100,
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid #e0e0e0',
                          '&:hover': {
                            boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                            '& .overlay': {
                              opacity: 1
                            }
                          }
                        }}
                        onClick={() => {
                          setCurrentImageIndex(index);
                          setIsFullscreenOpen(true);
                        }}
                      >
                        <Box
                          component="img"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          src={url}
                          alt={`Görev Resmi ${index + 1}`}
                        />
                        <Box 
                          className="overlay"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            py: 0.5,
                            px: 1,
                            fontSize: '0.75rem',
                            opacity: 0,
                            transition: 'opacity 0.2s'
                          }}
                        >
                          Görüntüle
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}

              {/* Yorumlar bölümü - Resimlerden sonra */}
              <Paper 
                variant="outlined" 
                sx={{ p: 2, mb: 3, borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <CommentIcon /> Yorumlar ({localComments.length})
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />

                {localComments.length > 0 ? (
                  <List>
                    {localComments.map(comment => (
                      <ListItem 
                        key={comment.id}
                        alignItems="flex-start"
                        sx={{ px: 0, py: 1.5 }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: comment.userId === currentUserId ? 'primary.main' : 'secondary.main' }}>
                            {comment.userName.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="subtitle2" fontWeight="bold">
                                {comment.userName} 
                                {comment.userId === currentUserId && (
                                  <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 1 }}>
                                    (Siz)
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(comment.createdAt)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                            >
                              {comment.text}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                    Henüz yorum yapılmamış.
                  </Typography>
                )}
              </Paper>

              {/* Yorum ekleme formu - Ayrı bir kart olarak */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <SectionTitle variant="subtitle1">
                  <SendIcon /> Yorum Ekle
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />
                
                {!canAddComment ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    En fazla 2 yorum yapabilirsiniz. Yorum limitinize ulaştınız.
                  </Alert>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Yorumunuzu buraya yazın (en fazla 300 karakter)..."
                      variant="outlined"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      disabled={submitting || !canAddComment}
                      error={!!commentError}
                      helperText={commentError || `${commentText.length}/300`}
                      InputProps={{
                        endAdornment: (
                          <Typography 
                            variant="caption" 
                            color={commentText.length > 300 ? "error" : "text.secondary"}
                            sx={{ position: 'absolute', right: 14, bottom: 14 }}
                          >
                            {commentText.length}/300
                          </Typography>
                        ),
                      }}
                    />
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                      <Typography variant="caption" color="text.secondary">
                        Kalan yorum hakkı: {2 - userCommentCount}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        endIcon={<SendIcon />}
                        onClick={handleSubmitComment}
                        disabled={!commentText.trim() || submitting || !canAddComment || commentText.length > 300}
                      >
                        {submitting ? 'Gönderiliyor...' : 'Yorum Gönder'}
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>

              {/* Gecikme açıklaması (varsa) */}
              {task.delayReason && (
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: 'error.light' }}
                >
                  <SectionTitle variant="subtitle1" color="error">
                    <AccessTimeIcon /> Gecikme Nedeni
                  </SectionTitle>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" whiteSpace="pre-wrap">
                    {task.delayReason}
                  </Typography>
                </Paper>
              )}
            </Grid>

            {/* Sağ taraf - Detaylar */}
            <Grid item xs={12} md={4} sx={{ 
              height: '100%', 
              overflowY: 'auto',
              // Webkit tarayıcılar için kaydırma çubuğu stilleri
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bdbdbd',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0a0a0',
              },
            }}>
              {/* Görev detayları */}
              <Paper 
                variant="outlined" 
                sx={{ p: 2, mb: 3, mt: 3, borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <AccessTimeIcon /> Görev Bilgileri
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Oluşturulma Tarihi
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(task.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Tamamlanma Tipi
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {task.completionType === 'button' ? 'Buton' : 'QR Kod'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Kabul Edilme Tarihi
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={task.acceptedAt ? 'text.primary' : 'text.secondary'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {task.acceptedAt && <TaskAltIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'primary.main' }} />}
                        {formatDate(task.acceptedAt)}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Başlama Tarihi
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={task.startedAt ? 'text.primary' : 'text.secondary'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {task.startedAt && <PlayCircleFilledIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'success.main' }} />}
                        {formatDate(task.startedAt)}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Tamamlanma Tarihi
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={task.completedAt ? 'text.primary' : 'text.secondary'}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {task.completedAt && <CheckCircleIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'secondary.main' }} />}
                        {formatDate(task.completedAt)}
                      </Box>
                    </Typography>
                  </Grid>
                  {task.deadline && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Son Tarih
                        </Typography>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color={task.status !== 'completed' ? 'error.main' : 'inherit'}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarTodayIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                            {formatDate(new Date(task.deadline).getTime())}
                          </Box>
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>

              {/* Atama bilgileri */}
              <Paper 
                variant="outlined" 
                sx={{ p: 2, mb: 3, borderRadius: 2 }}
              >
                <SectionTitle variant="subtitle1">
                  <PersonIcon /> Atama Bilgileri
                </SectionTitle>
                <Divider sx={{ mb: 2 }} />

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Atanan Personel
                  </Typography>
                  <Box display="flex" alignItems="center">
                    {loadingPersonnelName ? (
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    ) : (
                      <>
                        <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                          {personnelName ? personnelName.charAt(0).toUpperCase() : 'P'}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {personnelName || task.personnelName || 'Belirtilmemiş'}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Görev Grubu
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <CategoryIcon sx={{ fontSize: '1.2rem', mr: 0.5, color: 'text.secondary' }} />
                    {loadingGroupName ? (
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    ) : (
                      <Typography variant="body2" fontWeight="medium">
                        {groupName || task.groupName || 'Belirtilmemiş'}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {task.branchName && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Şube
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <BusinessIcon sx={{ fontSize: '1.2rem', mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight="medium">
                        {task.branchName}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* SAĞ TARAFA EKLENECEK Görev Tamamlama Fotoğrafı */}
                {task.completionPhotoUrl && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <SectionTitle variant="subtitle1">
                      <ImageIcon /> Görev Tamamlama Fotoğrafı
                    </SectionTitle>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        width: 100, // Thumbnail genişliği
                        height: 100, // Thumbnail yüksekliği
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          boxShadow: '0 0 8px rgba(0,0,0,0.2)',
                        },
                        mx: 'auto' // Ortalamak için
                      }}
                      onClick={() => setIsCompletionPhotoFullscreenOpen(true)}
                    >
                      <Box
                        component="img"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'cover'
                        }}
                        src={task.completionPhotoUrl}
                        alt="Görev Tamamlama Fotoğrafı"
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          console.error(`Tamamlama fotoğrafı yüklenirken hata: ${task.completionPhotoUrl}`);
                          e.currentTarget.src = 'https://via.placeholder.com/100?text=Hata';
                        }}
                      />
                    </Box>
                  </Paper>
                )}
              </Paper>

              {/* Tekrarlı görev bilgileri (varsa) */}
              {task.isRecurring && (
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, mb: 3, borderRadius: 2 }}
                >
                  {/* ... (Tekrar Bilgileri içeriği) ... */}
                </Paper>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {/* Tam ekran resim görüntüleme modalı */}
      <Dialog
        open={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            height: '90vh',
            m: 2,
            borderRadius: 2,
            position: 'relative'
          }
        }}
      >
        {/* Kapat butonu */}
        <IconButton
          onClick={() => setIsFullscreenOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)'
            },
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Resim görüntüleyici */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            position: 'relative'
          }}
        >
          {task.imageUrls && task.imageUrls.length > 0 && (
            <Box
              component="img"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
              src={task.imageUrls[currentImageIndex]}
              alt={`Görev Resmi ${currentImageIndex + 1}`}
            />
          )}

          {/* Sayfa numarası */}
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              py: 0.5,
              borderRadius: 2
            }}
          >
            {currentImageIndex + 1} / {task.imageUrls?.length || 0}
          </Typography>

          {/* Önceki/sonraki butonları */}
          {task.imageUrls && task.imageUrls.length > 1 && (
            <>
              <IconButton
                onClick={handlePreviousImage}
                disabled={currentImageIndex === 0}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <Typography variant="h3">←</Typography>
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                disabled={currentImageIndex === (task.imageUrls.length - 1)}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <Typography variant="h3">→</Typography>
              </IconButton>
            </>
          )}
        </Box>
      </Dialog>

      {/* EKLENDİ: Tam ekran tamamlama fotoğrafı görüntüleme modalı */}
      {task.completionPhotoUrl && (
        <Dialog
          open={isCompletionPhotoFullscreenOpen}
          onClose={() => setIsCompletionPhotoFullscreenOpen(false)}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              height: '90vh',
              m: 2,
              borderRadius: 2,
              position: 'relative'
            }
          }}
        >
          <IconButton
            onClick={() => setIsCompletionPhotoFullscreenOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              },
              zIndex: 10
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Box
              component="img"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
              src={task.completionPhotoUrl}
              alt="Görev Tamamlama Fotoğrafı"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                console.error(`Tam ekran tamamlama fotoğrafı yüklenirken hata: ${task.completionPhotoUrl}`);
                e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Resim+Yüklenemedi';
              }}
            />
          </Box>
        </Dialog>
      )}
    </Dialog>
  );
};

export default TaskDetailModal; 